import jsPDF from 'jspdf';
import { numberToFrenchText } from './numberToText.js';
import { formatDateOnlyFr } from './dateFormat.js';

export const generateQuotePDF = (quote, clientName, items = []) => {
  const doc = new jsPDF();

  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;
  const margin = 20;
  const contentWidth = pageWidth - (2 * margin);

  const primaryColor = [0, 51, 102];
  const textColor = [51, 51, 51];
  const lightGray = [245, 245, 245];
  const borderColor = [220, 220, 220];

  const cleanAmount = (amount) => {
    if (!amount) return 0;
    let clean = String(amount);
    clean = clean.replace(/^\d+\//, '');
    clean = clean.replace(/[^\d,.-]/g, '');
    clean = clean.replace(',', '.');
    const numAmount = parseFloat(clean);
    return isNaN(numAmount) ? 0 : numAmount;
  };

  const formatCurrency = (amount) => {
    const cleanedAmount = cleanAmount(amount);
    if (cleanedAmount === 0) return '0,00 DH';
    return new Intl.NumberFormat('fr-FR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      style: 'decimal',
      useGrouping: false
    }).format(cleanedAmount) + ' DH';
  };

  const formatDate = (dateString) => formatDateOnlyFr(dateString);

  const safeText = (text) => {
    if (!text) return '';
    return String(text).replace(/[^\x00-\x7F]/g, '');
  };

  const devisFileSlug = (q) => String(q.id).replace(/[/\\:*?"<>|]/g, '_');

  doc.setFillColor(...primaryColor);
  doc.rect(0, 0, pageWidth, 45, 'F');

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(22);
  doc.setFont('helvetica', 'bold');
  doc.text('EP OPTIQUE', margin, 18);

  doc.setFontSize(28);
  doc.setFont('helvetica', 'bold');
  doc.text('DEVIS', pageWidth - margin, 18, { align: 'right' });

  doc.setTextColor(...textColor);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text('DEVIS A:', margin, 65);

  doc.setFontSize(13);
  doc.text(safeText(clientName), margin, 75);

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(`DEVIS N°: ${quote.id}`, pageWidth - margin - 80, 65);
  doc.text(`DATE: ${formatDate(quote.date || quote.created_at)}`, pageWidth - margin - 80, 75);

  doc.setDrawColor(...borderColor);
  doc.setLineWidth(0.5);
  doc.line(margin, 85, pageWidth - margin, 85);

  const tableY = 100;

  doc.setFillColor(...primaryColor);
  doc.rect(margin, tableY, contentWidth, 15, 'F');

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('DESIGNATION', margin + 5, tableY + 10);
  doc.text('QUANTITE', margin + 100, tableY + 10);
  doc.text('PRIX UNIT.', margin + 150, tableY + 10);
  doc.text('MONTANT', pageWidth - margin - 50, tableY + 10);

  doc.setTextColor(...textColor);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');

  let yPosition = tableY + 25;
  let total = 0;
  const designationColumnMaxWidth = 88;
  const nameLineHeight = 4.5;

  if (items && items.length > 0) {
    items.forEach((item, index) => {
      const cleanPrice = cleanAmount(item.price || 0);
      const cleanQuantity = parseInt(item.quantity || 1, 10);
      const itemTotal = cleanPrice * cleanQuantity;
      total += itemTotal;

      const rowStartY = yPosition;
      const productName = item.product_name || `Produit ${item.product_id}`;

      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      const nameLines = doc.splitTextToSize(safeText(productName) || ' ', designationColumnMaxWidth);
      if (nameLines.length === 0) nameLines.push('-');
      const nameBlockHeight = Math.max(nameLineHeight, nameLines.length * nameLineHeight);
      const rowPadTop = 6;
      const rowPadBottom = 2;
      const totalRowHeight = rowPadTop + nameBlockHeight + rowPadBottom;

      if (index % 2 === 0) {
        doc.setFillColor(...lightGray);
        doc.rect(margin, rowStartY - rowPadTop, contentWidth, totalRowHeight, 'F');
      }

      doc.setTextColor(...textColor);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      let lineY = rowStartY;
      nameLines.forEach((line) => {
        doc.text(line, margin + 5, lineY);
        lineY += nameLineHeight;
      });

      doc.setFont('helvetica', 'normal');
      doc.text(String(cleanQuantity), margin + 100, rowStartY);
      doc.text(formatCurrency(cleanPrice), margin + 150, rowStartY);
      doc.setFont('helvetica', 'bold');
      doc.text(formatCurrency(itemTotal), pageWidth - margin - 50, rowStartY);

      yPosition = lineY + 2;
    });
  } else {
    const amount = cleanAmount(quote.amount || 0);
    total = amount;
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text('Montant du devis', margin + 5, yPosition);
    doc.setFont('helvetica', 'bold');
    doc.text(formatCurrency(amount), pageWidth - margin - 50, yPosition);
    yPosition += 12;
  }

  yPosition += 8;
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.text(
    'ARRETEZ LE DEVIS PRESENT A LA SOMME DE ' + numberToFrenchText(total).toUpperCase() + ' DIHRAMS',
    margin,
    yPosition
  );

  if (quote.note) {
    yPosition += 8;
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    const noteLines = doc.splitTextToSize(safeText(`Note: ${quote.note}`), contentWidth);
    noteLines.forEach((line) => {
      yPosition += 5;
      doc.text(line, margin, yPosition);
    });
  }

  yPosition += 12;
  doc.setDrawColor(...borderColor);
  doc.setLineWidth(0.5);
  doc.line(margin, yPosition, pageWidth - margin, yPosition);
  yPosition += 12;

  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text('TOTAL:', pageWidth - margin - 80, yPosition);
  doc.text(formatCurrency(total), pageWidth - margin - 20, yPosition);

  yPosition += 8;
  doc.setDrawColor(...borderColor);
  doc.setLineWidth(0.5);
  doc.line(margin, yPosition, pageWidth - margin, yPosition);

  const footerY = pageHeight - 50;

  doc.setFillColor(...lightGray);
  doc.rect(0, footerY, pageWidth, 50, 'F');

  doc.setDrawColor(...borderColor);
  doc.setLineWidth(0.5);
  doc.line(margin, footerY, pageWidth - margin, footerY);

  doc.setTextColor(...textColor);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');

  doc.text('STE EP OPTIQUE', margin, footerY + 12);
  doc.text('IF: 50215220', margin, footerY + 19);
  doc.text('RC: 54911', margin, footerY + 26);
  doc.text('Patente N: 17101768', margin, footerY + 33);

  doc.text('ICE: 002966305000046', pageWidth - margin - 80, footerY + 12);
  doc.text('TEL: 0764200156', pageWidth - margin - 80, footerY + 19);
  doc.text('INPE: 135031136', pageWidth - margin - 80, footerY + 26);
  doc.text('Email: contact@epoptique.ma', pageWidth - margin - 80, footerY + 33);

  doc.setFillColor(...primaryColor);
  doc.rect(0, pageHeight - 5, pageWidth, 5, 'F');

  doc.save(`Devis_${devisFileSlug(quote)}.pdf`);
};
